import type { ApiError } from './apiError.types';
import { getErrorMessages, getFirstErrorMessage } from './errorTranslator';
import type { ErrorTranslationOptions } from './errorUtils.types';
import { isApiError } from './typeGuards';

/**
 * Formats error(s) for display in notifications
 * Returns string for single error, array for multiple
 *
 * @param apiError - API error to format
 * @param options - Optional translation options
 * @returns String for single error or array of strings for multiple errors
 */
export function formatErrorsForDisplay(
  apiError: ApiError,
  options?: ErrorTranslationOptions,
): string | string[] {
  const messages = getErrorMessages(apiError, options);

  if (messages.length === 0) {
    return options?.t
      ? options.t('common.UnexpectedError' as any) // eslint-disable-line @typescript-eslint/no-explicit-any
      : 'common.UnexpectedError';
  }

  if (messages.length === 1) {
    return messages[0];
  }

  // Return messages as array for multi-line display
  return messages;
}

/**
 * Resolves an inline-displayable error message, or null if the error was
 * already handled globally (403 / 5xx toast) or is not an ApiError.
 *
 * @param error - Unknown error from a query or mutation
 * @param options - Optional translation options
 * @returns Translated message string, or null
 */
export function resolveInlineError(
  error: unknown,
  options?: ErrorTranslationOptions,
): string | null {
  if (!isApiError(error)) return null;
  return getFirstErrorMessage(error, options);
}
